


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type { StandardReportContentAndRoadParams } from '@/models/apis/externalReports/standard/standardReportRequest';
import type {
  StandardReportCar,
  StandardReport,
  SelectOption,
} from '@/models/externalReports/standard/spStandardReport';
import type {
  ExternalReportsLabelSp,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import {
  EXTERNAL_REPORT_CONTENTS_OTHER,
  CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS,
} from '@/consts/externalReports/standard';
import standardReportApi from '@/apis/externalReports/standard/standard_report';
import ConfirmModal from '@/components/lib/ConfirmModal.vue';
import { parseSelectionOptions } from '@/components/Sp/ExternalReports/Standard/utils';

interface SaveWorkSituationState {
  hanDisplayName: string;
  selectedContents: SelectOption[];
  selectedRoadNames: SelectOption[];
  otherContent: string;
  showConfirmContentAndRoadModal: boolean;
  showRevertEditContentAndRoadConfirmModal: boolean;
  standardReport: StandardReport;
}
export default defineComponent({
  props: {
    standardCar: {
      type: Object as PropType<StandardReportCar>,
      required: true,
    },
    externalReportAbility: {
      type: Object as PropType<ExternalReportAbility>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  components: {
    ConfirmModal,
  },
  emits: [
    'updateCommon',
    'revertScreen',
    'showErrorOnCommon',
  ],
  setup(props, { emit }) {
    const state = reactive<SaveWorkSituationState>({
      hanDisplayName: '',
      selectedContents: [],
      selectedRoadNames: [],
      otherContent: '',
      showConfirmContentAndRoadModal: false,
      showRevertEditContentAndRoadConfirmModal: false,
      standardReport: props.standardCar.report,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const showOtherTypeArea = computed<boolean>(() => {
      return state.selectedContents.some(e => e.name === EXTERNAL_REPORT_CONTENTS_OTHER);
    });

    const workContents = computed<SelectOption[]>(() => {
      return props.userSettings.choices.work_types.map((e, index) => ({ id: index, name: e }));
    });
    const roadNames = computed<SelectOption[]>(() => {
      return props.userSettings.choices.work_routes.map((e, index) => ({ id: index, name: e }));
    });
    const spLabels = computed<ExternalReportsLabelSp>(() => {
      return props.userSettings.sp;
    });
    const selectedContents = computed<string>(() => {
      return state.selectedContents.map(e => e.name).join(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS);
    });
    const selectedRoadNames = computed<string>(() => {
      return state.selectedRoadNames.map(e => e.name).join(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS);
    });

    const canSave = computed<boolean>(() => {
      return !isRequesting.value && checkInputs();
    });

    const isChanged = computed<boolean>(() => {
      const report = state.standardReport;
      const arraysHaveSameContent = (arr1: string, arr2: string): boolean => {
        const split1 = arr1.split(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS).sort();
        const split2 = arr2.split(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS).sort();
        return split1.length === split2.length && split1.every((item, index) => item === split2[index]);
      };
      if (!report.contents || !report.roadNames) return false;
      return !arraysHaveSameContent(report.contents, selectedContents.value) ||
        !arraysHaveSameContent(report.roadNames, selectedRoadNames.value) ||
        (report.otherContent || '') !== state.otherContent;
    });

    const checkInputs = (): boolean => {
      if (!state.hanDisplayName) return false;
      if (state.selectedContents.length === 0) return false;
      if (showOtherTypeArea.value && !state.otherContent) return false;
      if (state.selectedRoadNames.length === 0) return false;
      return true;
    };

    const tryRevertEditContentAndRoad = () => {
      if (isChanged.value) {
        state.showRevertEditContentAndRoadConfirmModal = true;
      } else {
        revertScreen();
      }
    };

    const revertScreen = () => {
      state.showRevertEditContentAndRoadConfirmModal = false;
      emit('revertScreen');
    };

    const saveContentAndRoad = async() => {
      if (!canSave.value) return;

      state.showConfirmContentAndRoadModal = false;
      const standardReport = state.standardReport;
      const params: StandardReportContentAndRoadParams = {
        contents: selectedContents.value,
        road_names: selectedRoadNames.value,
        other_content: state.otherContent,
      };
      await doRequest(async() => {
        state.standardReport = await standardReportApi.updateStandardReportContentAndRoad(standardReport.id, params);
      }, '保存に失敗しました。再度操作を行ってください');
      emit('updateCommon', null, state.standardReport);
    };

    onMounted(() => {
      if (!props.externalReportAbility || !props.externalReportAbility.han_name) return;

      const report = props.standardCar.report;
      state.hanDisplayName = props.externalReportAbility.han_name;
      if (!report || !report.contents || !report.roadNames) return;

      state.selectedContents = parseSelectionOptions(
        report.contents,
        props.userSettings.choices.work_types,
      );
      state.selectedRoadNames = parseSelectionOptions(
        report.roadNames,
        props.userSettings.choices.work_routes,
      );
      state.otherContent = report.otherContent || '';
    });

    return {
      state,
      canSave,
      isRequesting,
      isChanged,
      roadNames,
      showOtherTypeArea,
      spLabels,
      workContents,
      revertScreen,
      saveContentAndRoad,
      tryRevertEditContentAndRoad,
    };
  },
});
